import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  MousePointer,
  Hand,
  Share2,
  SquareEqual,
  Lightbulb,
  Grid2X2,
  Package,
  Sparkles,
  Ruler,
  Maximize2,
  ChevronRight,
  Plus,
  Cable,
  Square,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { sanitizeSVG } from '@/lib/sanitize';
import { unescapeSvg, resizeSvg } from '@/lib/svgNormalizer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAppStore } from '@/stores/appStore';
import { AIProductCreationModal } from '@/features/product-library/components/AIProductCreationModal';
import { CategoryIconRenderer } from '@/features/product-library/utils/categoryIcons';
import { categoryTemplateService, CategoryTemplate } from '@/services/categoryTemplateService';
import {
  useMergedCategoriesHierarchy,
  type MergedCategory,
} from '@/features/product-library/hooks/useMergedCategories';
import { useQuery } from '@tanstack/react-query';

type ControlType = 'keypad' | 'dimmer' | 'switch' | 'auxiliary' | 'centralized-keypad';
type ProductCategory = string; // Now dynamic from database

interface LeftToolbarProps {
  onToolSelect: (
    tool: 'select' | 'pan' | 'create-fixture' | 'create-strip' | 'create-panel' | 'create-room',
  ) => void;
  selectedTool:
    | 'select'
    | 'pan'
    | 'create-fixture'
    | 'create-strip'
    | 'create-panel'
    | 'create-room';
  onCircuitModeToggle: () => void;
  isCircuitMode: boolean;
  onControlTypeSelect: (
    type: 'keypad' | 'dimmer' | 'switch' | 'auxiliary' | 'centralized-keypad' | null,
  ) => void;
  selectedControlType: 'keypad' | 'dimmer' | 'switch' | 'auxiliary' | 'centralized-keypad' | null;
  isControlPlacementMode: boolean;
  onRoomCreationToggle?: () => void;
  isRoomCreationMode?: boolean;
  onProductCategorySelect?: (category: ProductCategory | null, subcategory?: string | null) => void;
  selectedProductCategory?: ProductCategory | null;
  isProductPlacementMode?: boolean;
  onFixtureCategorySelect?: (category: string | null, subcategory?: string | null) => void;
  selectedFixtureCategory?: string | null;
  selectedFixtureSubcategory?: string | null;
  isFixturePlacementMode?: boolean;
  onScaleToolClick?: () => void;
  isScaleModeActive?: boolean;
  onMeasurementToggle?: () => void;
  isMeasuring?: boolean;
  onElevationModeToggle?: () => void;
  isElevationMode?: boolean;
  hasScale?: boolean;
  hasRooms?: boolean;
  onPaletteOpen?: () => void;
  onCablePathModeToggle?: () => void;
  isCablePathMode?: boolean;
  onCableJunctionModeToggle?: () => void;
  isCableJunctionMode?: boolean;
}

export function LeftToolbar({
  onToolSelect,
  selectedTool,
  onCircuitModeToggle,
  isCircuitMode,
  onControlTypeSelect,
  selectedControlType,
  isControlPlacementMode,
  onRoomCreationToggle,
  isRoomCreationMode = false,
  onProductCategorySelect,
  selectedProductCategory,
  isProductPlacementMode = false,
  onFixtureCategorySelect,
  selectedFixtureCategory,
  selectedFixtureSubcategory,
  isFixturePlacementMode = false,
  onScaleToolClick,
  isScaleModeActive = false,
  onMeasurementToggle,
  isMeasuring = false,
  onElevationModeToggle,
  isElevationMode = false,
  hasScale = false,
  hasRooms = false,
  onPaletteOpen,
  onCablePathModeToggle,
  isCablePathMode = false,
  onCableJunctionModeToggle,
  isCableJunctionMode = false,
}: LeftToolbarProps) {
  // Local state
  const [showControlSubmenu, setShowControlSubmenu] = useState(false);
  const [showProductSubmenu, setShowProductSubmenu] = useState(false);
  const [showFixtureSubmenu, setShowFixtureSubmenu] = useState(false);
  const [showCableSubmenu, setShowCableSubmenu] = useState(false);
  const [aiProductModalOpen, setAiProductModalOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedFixtureCategories, setExpandedFixtureCategories] = useState<Set<string>>(
    new Set(),
  );
  const isFixtureToolActive =
    selectedTool === 'create-fixture' || selectedTool === 'create-strip' || isFixturePlacementMode;

  const clearStripTool = () => {
    if (selectedTool === 'create-strip') {
      onToolSelect('select');
    }
  };
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);

  // Get spacebar state from store
  const isSpacePressed = useAppStore(state => state.ui.toolbar.isSpacePressed);

  // Refs
  const controlButtonRef = useRef<HTMLButtonElement>(null);
  const controlSubmenuRef = useRef<HTMLDivElement>(null);
  const productButtonRef = useRef<HTMLButtonElement>(null);
  const productSubmenuRef = useRef<HTMLDivElement>(null);
  const fixtureButtonRef = useRef<HTMLButtonElement>(null);
  const fixtureSubmenuRef = useRef<HTMLDivElement>(null);
  const cableButtonRef = useRef<HTMLButtonElement>(null);
  const cableSubmenuRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const productCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fixtureCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const cableCloseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle control submenu hover
  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    // Instantly close other submenus
    setShowProductSubmenu(false);
    setShowFixtureSubmenu(false);
    if (productCloseTimeoutRef.current) {
      clearTimeout(productCloseTimeoutRef.current);
      productCloseTimeoutRef.current = null;
    }
    if (fixtureCloseTimeoutRef.current) {
      clearTimeout(fixtureCloseTimeoutRef.current);
      fixtureCloseTimeoutRef.current = null;
    }

    setShowControlSubmenu(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setShowControlSubmenu(false);
    }, 200);
  };

  // Handle control button click
  const handleControlButtonClick = () => {
    if (isControlPlacementMode) {
      onControlTypeSelect(null);
    } else if (selectedControlType) {
      onControlTypeSelect(selectedControlType);
    }
  };

  // Handle control type selection
  const handleControlTypeSelect = (type: ControlType) => {
    // Clear other modes first
    clearStripTool();
    if (isProductPlacementMode) {
      onProductCategorySelect?.(null);
    }
    // Clear fixture placement mode
    if (isFixturePlacementMode) {
      onFixtureCategorySelect?.(null);
    }
    // Clear circuit mode
    if (isCircuitMode) {
      onCircuitModeToggle();
    }
    // Clear elevation mode
    if (isElevationMode) {
      onElevationModeToggle?.();
    }
    // Clear measurement mode
    if (isMeasuring) {
      onMeasurementToggle?.();
    }
    // Clear cable path mode
    if (isCablePathMode) {
      onCablePathModeToggle?.();
    }

    onControlTypeSelect(type);
    setShowControlSubmenu(false);
  };

  // Handle product submenu hover
  const handleProductMouseEnter = () => {
    if (productCloseTimeoutRef.current) {
      clearTimeout(productCloseTimeoutRef.current);
      productCloseTimeoutRef.current = null;
    }

    // Instantly close other submenus
    setShowControlSubmenu(false);
    setShowFixtureSubmenu(false);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (fixtureCloseTimeoutRef.current) {
      clearTimeout(fixtureCloseTimeoutRef.current);
      fixtureCloseTimeoutRef.current = null;
    }

    setShowProductSubmenu(true);
  };

  const handleProductMouseLeave = () => {
    productCloseTimeoutRef.current = setTimeout(() => {
      setShowProductSubmenu(false);
    }, 200);
  };

  // Handle product button click
  const handleProductButtonClick = () => {
    if (isProductPlacementMode) {
      onProductCategorySelect?.(null);
    } else if (selectedProductCategory) {
      onProductCategorySelect?.(selectedProductCategory);
    } else {
      // Toggle submenu if no category is selected
      setShowProductSubmenu(!showProductSubmenu);
    }
  };

  // Fetch LV product categories from database (merged: system + org-specific)
  const { hierarchy: mergedCategoryHierarchy, isLoading: isMergedLoading } =
    useMergedCategoriesHierarchy('lv_products');

  // Fetch fixture categories from database
  const { data: fixtureCategoryTemplates = [] } = useQuery<CategoryTemplate[]>({
    queryKey: ['category-templates', 'lighting_fixtures'],
    queryFn: () => categoryTemplateService.getByDomain('lighting_fixtures'),
  });

  // Fetch control categories from database
  const { data: controlCategoryTemplates = [] } = useQuery<CategoryTemplate[]>({
    queryKey: ['category-templates', 'lighting_controls'],
    queryFn: () => categoryTemplateService.getByDomain('lighting_controls'),
  });

  // Helper to get control template by subcategory
  const getControlTemplate = (subcategory: ControlType): CategoryTemplate | undefined => {
    return controlCategoryTemplates.find(t => t.subcategory === subcategory);
  };

  // LV product categories hierarchy is now provided by useMergedCategoriesHierarchy hook
  // which merges system templates with organization-specific custom categories

  // Organize fixture categories into hierarchy
  const fixtureCategoryHierarchy = useMemo(() => {
    const hierarchy = new Map<string, CategoryTemplate[]>();
    const parentCategories: CategoryTemplate[] = [];

    fixtureCategoryTemplates.forEach(template => {
      if (!template.subcategory) {
        // This is a parent category
        parentCategories.push(template);
        if (!hierarchy.has(template.category)) {
          hierarchy.set(template.category, []);
        }
      } else {
        // This is a subcategory
        const parentKey = template.parent_category || template.category;
        if (!hierarchy.has(parentKey)) {
          hierarchy.set(parentKey, []);
        }
        hierarchy.get(parentKey)!.push(template);
      }
    });

    const comparator = (a: CategoryTemplate, b: CategoryTemplate) => {
      const orderA = a.display_order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.display_order ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.display_name.localeCompare(b.display_name, undefined, { sensitivity: 'base' });
    };

    parentCategories.sort(comparator);
    hierarchy.forEach(list => list.sort(comparator));

    return { parents: parentCategories, children: hierarchy };
  }, [fixtureCategoryTemplates]);

  // Handle category expansion toggle
  const toggleCategoryExpansion = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Handle fixture category expansion toggle
  const toggleFixtureCategoryExpansion = (category: string) => {
    setExpandedFixtureCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Handle product category selection
  const handleProductCategorySelect = (category: ProductCategory, subcategory?: string | null) => {
    // Clear other modes first
    clearStripTool();
    if (isControlPlacementMode) {
      onControlTypeSelect(null);
    }
    if (isFixturePlacementMode) {
      onFixtureCategorySelect?.(null);
    }
    // Clear circuit mode
    if (isCircuitMode) {
      onCircuitModeToggle();
    }
    // Clear elevation mode
    if (isElevationMode) {
      onElevationModeToggle?.();
    }
    // Clear measurement mode
    if (isMeasuring) {
      onMeasurementToggle?.();
    }
    // Clear cable path mode
    if (isCablePathMode) {
      onCablePathModeToggle?.();
    }

    setSelectedSubcategory(subcategory || null);
    onProductCategorySelect?.(category, subcategory);
    setShowProductSubmenu(false);
  };

  // Handle fixture category selection
  const handleFixtureCategorySelect = (category: string | null, subcategory?: string | null) => {
    // Clear other modes first
    if (isControlPlacementMode) {
      onControlTypeSelect(null);
    }
    if (isProductPlacementMode) {
      onProductCategorySelect?.(null);
    }
    // Clear circuit mode
    if (isCircuitMode) {
      onCircuitModeToggle();
    }
    // Clear elevation mode
    if (isElevationMode) {
      onElevationModeToggle?.();
    }
    // Clear measurement mode
    if (isMeasuring) {
      onMeasurementToggle?.();
    }
    // Clear cable path mode
    if (isCablePathMode) {
      onCablePathModeToggle?.();
    }

    // Pass the selection to the parent component
    onFixtureCategorySelect?.(category, subcategory);

    // Always close the submenu after selection
    setShowFixtureSubmenu(false);

    // Don't automatically activate the tool - let the parent handle tool activation
    // This prevents the fixture tool from staying selected when other tools are clicked
  };

  // Handle fixture submenu hover
  const handleFixtureMouseEnter = () => {
    if (fixtureCloseTimeoutRef.current) {
      clearTimeout(fixtureCloseTimeoutRef.current);
      fixtureCloseTimeoutRef.current = null;
    }

    // Instantly close other submenus
    setShowControlSubmenu(false);
    setShowProductSubmenu(false);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (productCloseTimeoutRef.current) {
      clearTimeout(productCloseTimeoutRef.current);
      productCloseTimeoutRef.current = null;
    }

    setShowFixtureSubmenu(true);
  };

  const handleFixtureMouseLeave = () => {
    fixtureCloseTimeoutRef.current = setTimeout(() => {
      setShowFixtureSubmenu(false);
    }, 200);
  };

  // Handle fixture button click - only toggles submenu
  const handleFixtureButtonClick = () => {
    // Only toggle submenu, don't change tool state
    setShowFixtureSubmenu(!showFixtureSubmenu);
  };

  // Handle cable submenu hover
  const handleCableMouseEnter = () => {
    if (cableCloseTimeoutRef.current) {
      clearTimeout(cableCloseTimeoutRef.current);
      cableCloseTimeoutRef.current = null;
    }

    // Instantly close other submenus
    setShowControlSubmenu(false);
    setShowProductSubmenu(false);
    setShowFixtureSubmenu(false);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (productCloseTimeoutRef.current) {
      clearTimeout(productCloseTimeoutRef.current);
      productCloseTimeoutRef.current = null;
    }
    if (fixtureCloseTimeoutRef.current) {
      clearTimeout(fixtureCloseTimeoutRef.current);
      fixtureCloseTimeoutRef.current = null;
    }

    setShowCableSubmenu(true);
  };

  const handleCableMouseLeave = () => {
    cableCloseTimeoutRef.current = setTimeout(() => {
      setShowCableSubmenu(false);
    }, 200);
  };

  // Handle cable tool selection from submenu
  const handleCableToolSelect = (toolType: 'path' | 'box') => {
    // Close the submenu
    setShowCableSubmenu(false);

    // Clear other modes first
    clearStripTool();
    if (isControlPlacementMode) {
      onControlTypeSelect(null);
    }
    if (isProductPlacementMode) {
      onProductCategorySelect?.(null);
    }
    if (isFixturePlacementMode) {
      onFixtureCategorySelect?.(null);
    }
    if (isCircuitMode) {
      onCircuitModeToggle();
    }
    if (isElevationMode) {
      onElevationModeToggle?.();
    }
    if (isMeasuring) {
      onMeasurementToggle?.();
    }
    if (selectedTool === 'create-room') {
      onToolSelect('select');
    }

    // Activate the selected cable tool
    if (toolType === 'path') {
      // Stop junction mode if active, then toggle path mode
      if (isCableJunctionMode) {
        onCableJunctionModeToggle?.();
      }
      onCablePathModeToggle?.();
    } else if (toolType === 'box') {
      // Stop path mode if active, then toggle junction mode
      if (isCablePathMode) {
        onCablePathModeToggle?.();
      }
      onCableJunctionModeToggle?.();
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (productCloseTimeoutRef.current) {
        clearTimeout(productCloseTimeoutRef.current);
      }
      if (fixtureCloseTimeoutRef.current) {
        clearTimeout(fixtureCloseTimeoutRef.current);
      }
      if (cableCloseTimeoutRef.current) {
        clearTimeout(cableCloseTimeoutRef.current);
      }
    };
  }, []);

  // Handle clicks outside submenus
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        showControlSubmenu &&
        controlButtonRef.current &&
        controlSubmenuRef.current &&
        !controlButtonRef.current.contains(event.target as Node) &&
        !controlSubmenuRef.current.contains(event.target as Node)
      ) {
        setShowControlSubmenu(false);
      }

      if (
        showProductSubmenu &&
        productButtonRef.current &&
        productSubmenuRef.current &&
        !productButtonRef.current.contains(event.target as Node) &&
        !productSubmenuRef.current.contains(event.target as Node)
      ) {
        setShowProductSubmenu(false);
      }

      if (
        showFixtureSubmenu &&
        fixtureButtonRef.current &&
        fixtureSubmenuRef.current &&
        !fixtureButtonRef.current.contains(event.target as Node) &&
        !fixtureSubmenuRef.current.contains(event.target as Node)
      ) {
        setShowFixtureSubmenu(false);
      }

      if (
        showCableSubmenu &&
        cableButtonRef.current &&
        cableSubmenuRef.current &&
        !cableButtonRef.current.contains(event.target as Node) &&
        !cableSubmenuRef.current.contains(event.target as Node)
      ) {
        setShowCableSubmenu(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showControlSubmenu, showProductSubmenu, showFixtureSubmenu, showCableSubmenu]);

  // Handle scale/measurement tool click
  const handleScaleToolClick = () => {
    // Clear other modes first
    clearStripTool();
    if (isControlPlacementMode) {
      onControlTypeSelect(null);
    }
    if (isProductPlacementMode) {
      onProductCategorySelect?.(null);
    }
    // Clear fixture placement mode
    if (isFixturePlacementMode) {
      onFixtureCategorySelect?.(null);
    }
    // Clear circuit mode
    if (isCircuitMode) {
      onCircuitModeToggle();
    }
    // Clear elevation mode
    if (isElevationMode) {
      onElevationModeToggle?.();
    }
    // Clear room creation mode
    if (selectedTool === 'create-room') {
      onToolSelect('select');
    }
    // Clear cable path mode
    if (isCablePathMode) {
      onCablePathModeToggle?.();
    }

    // Call the original scale tool handler
    onScaleToolClick?.();
  };

  // Handle circuit/load tool click
  const handleCircuitToolClick = () => {
    clearStripTool();

    // Clear cable path mode
    if (isCablePathMode) {
      onCablePathModeToggle?.();
    }
    // Call the circuit mode toggle
    onCircuitModeToggle();
  };

  // Handle elevation tool click
  const handleElevationToolClick = () => {
    clearStripTool();

    // Clear measurement mode
    if (isMeasuring) {
      onMeasurementToggle?.();
    }
    // Clear cable path mode
    if (isCablePathMode) {
      onCablePathModeToggle?.();
    }

    // Call the elevation mode toggle
    onElevationModeToggle?.();
  };

  // Handle room tool click
  const handleRoomToolClick = () => {
    const isTogglingToRoom = selectedTool !== 'create-room';
    clearStripTool();

    // Always clear other modes when toggling TO room mode
    // OR when already in room mode and clicking again (to ensure cleanup)
    if (isTogglingToRoom || selectedTool === 'create-room') {
      // Clear measurement mode
      if (isMeasuring) {
        onMeasurementToggle?.();
      }
      // Clear cable path mode
      if (isCablePathMode) {
        onCablePathModeToggle?.();
      }
    }

    // Toggle between room creation and select
    const newTool = selectedTool === 'create-room' ? 'select' : 'create-room';
    onToolSelect(newTool);
  };

  // Handle cable path tool click
  const handleCablePathToolClick = () => {
    // Clear other modes first
    clearStripTool();
    if (isControlPlacementMode) {
      onControlTypeSelect(null);
    }
    if (isProductPlacementMode) {
      onProductCategorySelect?.(null);
    }
    if (isFixturePlacementMode) {
      onFixtureCategorySelect?.(null);
    }
    if (isCircuitMode) {
      onCircuitModeToggle();
    }
    if (isElevationMode) {
      onElevationModeToggle?.();
    }
    if (isMeasuring) {
      onMeasurementToggle?.();
    }
    if (selectedTool === 'create-room') {
      onToolSelect('select');
    }

    // Toggle cable path mode
    onCablePathModeToggle?.();
  };

  // Helper function to render category icon - SIMPLIFIED
  // SVGs are normalized at upload time, so we just apply CSS color here
  const renderCategoryIcon = (category: CategoryTemplate | MergedCategory, size: number = 14) => {
    if (category.icon_svg) {
      const cleanedSvg = unescapeSvg(category.icon_svg);
      const resizedSvg = resizeSvg(cleanedSvg, size, size);

      // Get color - CategoryTemplate uses default_color, MergedCategory uses fill_color
      const iconColor =
        ('default_color' in category ? category.default_color : null) ||
        ('fill_color' in category ? category.fill_color : null) ||
        '#6B7280';

      return (
        <div
          className="flex items-center justify-center"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            color: iconColor,
          }}
          dangerouslySetInnerHTML={{
            __html: sanitizeSVG(resizedSvg),
          }}
        />
      );
    }

    if ('icon_url' in category && category.icon_url) {
      return (
        <img
          src={category.icon_url}
          alt={category.display_name}
          style={{ width: `${size}px`, height: `${size}px` }}
          className="object-contain"
        />
      );
    }

    // For MergedCategory with abbreviation, render as colored box with text
    if ('icon_type' in category && category.icon_type === 'abbreviation' && category.abbreviation) {
      const fillColor = category.fill_color || '#6B7280';
      const textColor = category.text_color || '#FFFFFF';
      return (
        <div
          className="flex items-center justify-center rounded-sm font-bold"
          style={{
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: fillColor,
            color: textColor,
            fontSize: `${Math.max(8, size * 0.65)}px`,
          }}
        >
          {category.abbreviation.substring(0, 3)}
        </div>
      );
    }

    // Fallback to default icon
    return null;
  };

  const getSubmenuStyle = (buttonRef: React.RefObject<HTMLButtonElement>): React.CSSProperties => {
    const rect = buttonRef.current?.getBoundingClientRect();
    const fallbackTop = 104;
    const fallbackLeft = 80;

    if (!rect) {
      return {
        top: fallbackTop,
        left: fallbackLeft,
        maxHeight: `calc(100vh - ${fallbackTop + 16}px)`,
      };
    }

    const top = Math.max(96, rect.top);
    return {
      top,
      left: rect.right + 8,
      maxHeight: Math.max(180, window.innerHeight - top - 16),
    };
  };

  return (
    <TooltipProvider>
      <div className="fixed left-0 top-[100px] bottom-0 z-[1000] flex w-[72px] items-stretch overflow-visible border-r border-slate-200/80 bg-white/95 shadow-[1px_0_0_rgba(15,23,42,0.03)] backdrop-blur">
        <div className="relative flex h-full w-full flex-col items-center space-y-2 overflow-y-auto overflow-x-visible px-2 py-3 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400">
          <div className="flex flex-col space-y-2">
            {/* Selection Tool */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={selectedTool === 'select' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => onToolSelect('select')}
                  className={selectedTool === 'select' ? 'bg-primary text-primary-foreground' : ''}
                >
                  <MousePointer className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Select</p>
              </TooltipContent>
            </Tooltip>

            {/* Pan Tool */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={selectedTool === 'pan' || isSpacePressed ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => onToolSelect('pan')}
                  className={
                    selectedTool === 'pan' || isSpacePressed
                      ? 'bg-primary text-primary-foreground'
                      : ''
                  }
                >
                  <Hand className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Pan (Hold Space)</p>
              </TooltipContent>
            </Tooltip>
          </div>

          <Separator className="my-1" />

          {/* Create Fixture Tool with Submenu */}
          <div
            className="relative"
            onMouseEnter={handleFixtureMouseEnter}
            onMouseLeave={handleFixtureMouseLeave}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  ref={fixtureButtonRef}
                  variant={isFixtureToolActive ? 'default' : 'ghost'}
                  size="icon"
                  onClick={handleFixtureButtonClick}
                  className={cn(
                    'relative',
                    isFixtureToolActive ? 'bg-primary text-primary-foreground' : '',
                  )}
                >
                  <Lightbulb className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Fixtures</p>
              </TooltipContent>
            </Tooltip>

            {/* Fixture Submenu */}
            {showFixtureSubmenu &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  ref={fixtureSubmenuRef}
                  className={cn(
                    'fixed w-[260px] overflow-y-auto overflow-x-hidden rounded-md border border-slate-200/80 bg-white p-2 shadow-[0_18px_45px_rgba(15,23,42,0.16)] z-[1100]',
                    'scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400',
                  )}
                  style={getSubmenuStyle(fixtureButtonRef)}
                  onMouseEnter={handleFixtureMouseEnter}
                  onMouseLeave={handleFixtureMouseLeave}
                >
                  {/* Bridge element */}
                  <div className="absolute -left-2 top-0 h-full w-2"></div>

                  {/* Fixture Marker Option - Always at top */}
                  <Button
                    variant={
                      selectedTool === 'create-fixture' && !selectedFixtureCategory
                        ? 'secondary'
                        : 'ghost'
                    }
                    size="sm"
                    className={cn(
                      'w-full justify-start h-8 px-2 text-xs font-normal mb-2',
                      selectedTool === 'create-fixture' &&
                        !selectedFixtureCategory &&
                        'bg-primary/10 text-primary hover:bg-primary/20',
                    )}
                    onClick={() => {
                      // Select fixture marker (no category/subcategory)
                      handleFixtureCategorySelect(null, null);
                    }}
                  >
                    <div className="w-4 h-4 mr-2 rounded-full border-2 border-blue-700 bg-blue-500 flex items-center justify-center">
                      <div className="w-1.5 h-1.5 rounded-full bg-white" />
                    </div>
                    <span>Fixture Marker</span>
                  </Button>

                  {/* Strip Lighting Option - For LED strips */}
                  <Button
                    variant={selectedTool === 'create-strip' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={cn(
                      'w-full justify-start h-8 px-2 text-xs font-normal mb-2',
                      selectedTool === 'create-strip' &&
                        'bg-primary/10 text-primary hover:bg-primary/20',
                    )}
                    onClick={() => {
                      // Clear other modes and select strip tool
                      if (isControlPlacementMode) {
                        onControlTypeSelect(null);
                      }
                      if (isProductPlacementMode) {
                        onProductCategorySelect?.(null);
                      }
                      if (isFixturePlacementMode) {
                        onFixtureCategorySelect?.(null);
                      }
                      if (isCircuitMode) {
                        onCircuitModeToggle();
                      }
                      if (isMeasuring) {
                        onMeasurementToggle?.();
                      }
                      setShowFixtureSubmenu(false);
                      onToolSelect('create-strip');
                    }}
                  >
                    <div className="w-4 h-4 mr-2 flex items-center justify-center">
                      {/* Dashed line icon - amber/warm LED color */}
                      <svg width="16" height="4" viewBox="0 0 16 4">
                        <line
                          x1="0"
                          y1="2"
                          x2="16"
                          y2="2"
                          stroke="#fbbf24"
                          strokeWidth="3"
                          strokeDasharray="4 2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                    <span>Strip Lighting</span>
                  </Button>

                  <Separator className="my-2" />

                  {/* Scrollable content wrapper for categories */}
                  <div className="space-y-1 pr-1">
                    {/* Render parent categories */}
                    {fixtureCategoryHierarchy.parents.map(parent => {
                      const hasSubcategories =
                        (fixtureCategoryHierarchy.children.get(parent.category) || []).length > 0;
                      const isExpanded = expandedFixtureCategories.has(parent.category);
                      const isSelected =
                        selectedFixtureCategory === parent.category && !selectedFixtureSubcategory;

                      return (
                        <div key={parent.id}>
                          {/* Parent Category Button */}
                          <Button
                            variant={isSelected ? 'secondary' : 'ghost'}
                            size="sm"
                            className={cn(
                              'w-full justify-start h-8 px-2 text-xs font-normal',
                              isSelected && 'bg-primary/10 text-primary hover:bg-primary/20',
                            )}
                            onClick={() => {
                              if (hasSubcategories) {
                                toggleFixtureCategoryExpansion(parent.category);
                              } else {
                                handleFixtureCategorySelect(parent.category);
                              }
                            }}
                          >
                            {hasSubcategories && (
                              <ChevronRight
                                className={cn(
                                  'h-3 w-3 mr-1 transition-transform flex-shrink-0',
                                  isExpanded && 'rotate-90',
                                )}
                              />
                            )}
                            {!hasSubcategories && <div className="w-4 mr-1" />}

                            {/* Render parent icon */}
                            <div className="mr-2 flex-shrink-0">
                              {renderCategoryIcon(parent, 14) || (
                                <Lightbulb className="h-[14px] w-[14px]" />
                              )}
                            </div>

                            <span className="truncate">{parent.display_name}</span>
                          </Button>

                          {/* Subcategories (if expanded) */}
                          {hasSubcategories && isExpanded && (
                            <div className="ml-4 mt-0.5 space-y-0.5">
                              {/* Render subcategories */}
                              {(fixtureCategoryHierarchy.children.get(parent.category) || []).map(
                                sub => {
                                  const isSubSelected =
                                    selectedFixtureCategory === parent.category &&
                                    selectedFixtureSubcategory === sub.subcategory;

                                  return (
                                    <Button
                                      key={sub.id}
                                      variant={isSubSelected ? 'secondary' : 'ghost'}
                                      size="sm"
                                      className={cn(
                                        'w-full justify-start h-7 px-2 text-[11px] font-normal',
                                        isSubSelected &&
                                          'bg-primary/10 text-primary hover:bg-primary/20',
                                      )}
                                      onClick={() =>
                                        handleFixtureCategorySelect(
                                          parent.category,
                                          sub.subcategory,
                                        )
                                      }
                                    >
                                      <div className="mr-2 flex-shrink-0 opacity-75">
                                        {renderCategoryIcon(sub, 12) ||
                                          renderCategoryIcon(parent, 12) || (
                                            <Lightbulb className="h-[12px] w-[12px] opacity-75" />
                                          )}
                                      </div>
                                      <span className="truncate">{sub.display_name}</span>
                                    </Button>
                                  );
                                },
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>,
                document.body,
              )}
          </div>

          {/* Load Creation Tool (Circuit Mode) */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isCircuitMode ? 'default' : 'ghost'}
                size="icon"
                onClick={handleCircuitToolClick}
                className={isCircuitMode ? 'bg-primary text-primary-foreground' : ''}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Load Creation Tool</p>
            </TooltipContent>
          </Tooltip>

          {/* Controls Menu */}
          <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  ref={controlButtonRef}
                  variant={isControlPlacementMode ? 'default' : 'ghost'}
                  size="icon"
                  className={cn(
                    'relative',
                    isControlPlacementMode ? 'bg-primary text-primary-foreground' : '',
                  )}
                  onClick={handleControlButtonClick}
                >
                  <SquareEqual className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Controls</p>
              </TooltipContent>
            </Tooltip>

            {/* Control Submenu */}
            {showControlSubmenu &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  ref={controlSubmenuRef}
                  className="fixed flex min-w-[150px] flex-col space-y-2 overflow-y-auto rounded-md border border-slate-200/80 bg-white p-2 shadow-[0_18px_45px_rgba(15,23,42,0.16)] z-[1100]"
                  style={getSubmenuStyle(controlButtonRef)}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Bridge element */}
                  <div className="absolute -left-2 top-0 h-full w-2"></div>

                  {/* Local Controls Section */}
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Local Controls
                  </div>

                  <Button
                    variant={selectedControlType === 'keypad' ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'justify-start text-sm',
                      selectedControlType === 'keypad' ? 'bg-primary text-primary-foreground' : '',
                    )}
                    onClick={() => handleControlTypeSelect('keypad')}
                  >
                    {getControlTemplate('keypad')?.icon_svg ? (
                      <CategoryIconRenderer
                        template={getControlTemplate('keypad')!}
                        size={16}
                        className="mr-2 flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-4 h-4 mr-2 border flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: '#3b82f6', backgroundColor: 'transparent' }}
                      >
                        <span className="text-[10px] font-bold" style={{ color: '#3b82f6' }}>
                          K
                        </span>
                      </div>
                    )}
                    Keypad
                  </Button>

                  <Button
                    variant={selectedControlType === 'dimmer' ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'justify-start text-sm',
                      selectedControlType === 'dimmer' ? 'bg-primary text-primary-foreground' : '',
                    )}
                    onClick={() => handleControlTypeSelect('dimmer')}
                  >
                    {getControlTemplate('dimmer')?.icon_svg ? (
                      <CategoryIconRenderer
                        template={getControlTemplate('dimmer')!}
                        size={16}
                        className="mr-2 flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-4 h-4 mr-2 border flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: '#fb923c', backgroundColor: 'transparent' }}
                      >
                        <span className="text-[10px] font-bold" style={{ color: '#fb923c' }}>
                          D
                        </span>
                      </div>
                    )}
                    Dimmer
                  </Button>

                  <Button
                    variant={selectedControlType === 'switch' ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'justify-start text-sm',
                      selectedControlType === 'switch' ? 'bg-primary text-primary-foreground' : '',
                    )}
                    onClick={() => handleControlTypeSelect('switch')}
                  >
                    {getControlTemplate('switch')?.icon_svg ? (
                      <CategoryIconRenderer
                        template={getControlTemplate('switch')!}
                        size={16}
                        className="mr-2 flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-4 h-4 mr-2 border flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: '#22c55e', backgroundColor: 'transparent' }}
                      >
                        <span className="text-[10px] font-bold" style={{ color: '#22c55e' }}>
                          S
                        </span>
                      </div>
                    )}
                    Switch
                  </Button>

                  <Button
                    variant={selectedControlType === 'auxiliary' ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'justify-start text-sm',
                      selectedControlType === 'auxiliary'
                        ? 'bg-primary text-primary-foreground'
                        : '',
                    )}
                    onClick={() => handleControlTypeSelect('auxiliary')}
                  >
                    {getControlTemplate('auxiliary')?.icon_svg ? (
                      <CategoryIconRenderer
                        template={getControlTemplate('auxiliary')!}
                        size={16}
                        className="mr-2 flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-4 h-4 mr-2 border flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: '#a16207', backgroundColor: 'transparent' }}
                      >
                        <span className="text-[10px] font-bold" style={{ color: '#a16207' }}>
                          A
                        </span>
                      </div>
                    )}
                    Auxiliary
                  </Button>

                  {/* Centralized Controls Section */}
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground mt-2">
                    Centralized Controls
                  </div>

                  <Button
                    variant={selectedControlType === 'centralized-keypad' ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'justify-start text-sm',
                      selectedControlType === 'centralized-keypad'
                        ? 'bg-primary text-primary-foreground'
                        : '',
                    )}
                    onClick={() => handleControlTypeSelect('centralized-keypad')}
                  >
                    {getControlTemplate('centralized-keypad')?.icon_svg ? (
                      <CategoryIconRenderer
                        template={getControlTemplate('centralized-keypad')!}
                        size={16}
                        className="mr-2 flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-4 h-4 mr-2 border flex items-center justify-center flex-shrink-0"
                        style={{ borderColor: '#1e3a8a', backgroundColor: '#1e40af' }}
                      >
                        <span className="text-[10px] font-bold text-white">K</span>
                      </div>
                    )}
                    LV Keypad
                  </Button>
                </div>,
                document.body,
              )}
          </div>

          {/* Products Menu */}
          <div
            className="relative"
            onMouseEnter={handleProductMouseEnter}
            onMouseLeave={handleProductMouseLeave}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  ref={productButtonRef}
                  variant={isProductPlacementMode ? 'default' : 'ghost'}
                  size="icon"
                  className={cn(
                    'relative',
                    isProductPlacementMode ? 'bg-primary text-primary-foreground' : '',
                  )}
                  onClick={handleProductButtonClick}
                >
                  <Package className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Products</p>
              </TooltipContent>
            </Tooltip>

            {/* Product Submenu */}
            {showProductSubmenu &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  ref={productSubmenuRef}
                  className={cn(
                    'fixed w-[260px] overflow-y-auto overflow-x-hidden rounded-md border border-slate-200/80 bg-white p-2 shadow-[0_18px_45px_rgba(15,23,42,0.16)] z-[1100]',
                    'scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent hover:scrollbar-thumb-slate-400',
                  )}
                  style={getSubmenuStyle(productButtonRef)}
                  onMouseEnter={handleProductMouseEnter}
                  onMouseLeave={handleProductMouseLeave}
                >
                  {/* Bridge element */}
                  <div className="absolute -left-2 top-0 h-full w-2"></div>

                  {/* Scrollable content wrapper */}
                  <div className="space-y-1 pr-1">
                    {/* Render parent categories (merged: system + org-specific) */}
                    {mergedCategoryHierarchy.parents.map(parent => {
                      const hasSubcategories =
                        (mergedCategoryHierarchy.children.get(parent.category) || []).length > 0;
                      const isExpanded = expandedCategories.has(parent.category);
                      const isSelected =
                        selectedProductCategory === parent.category && !selectedSubcategory;

                      return (
                        <div key={parent.id}>
                          {/* Parent Category Button */}
                          <Button
                            variant={isSelected ? 'secondary' : 'ghost'}
                            size="sm"
                            className={cn(
                              'w-full justify-start h-8 px-2 text-xs font-normal',
                              isSelected && 'bg-primary/10 text-primary hover:bg-primary/20',
                            )}
                            onClick={() => {
                              if (hasSubcategories) {
                                toggleCategoryExpansion(parent.category);
                              } else {
                                handleProductCategorySelect(parent.category);
                              }
                            }}
                          >
                            {hasSubcategories && (
                              <ChevronRight
                                className={cn(
                                  'h-3 w-3 mr-1 transition-transform flex-shrink-0',
                                  isExpanded && 'rotate-90',
                                )}
                              />
                            )}
                            {!hasSubcategories && <div className="w-4 mr-1" />}

                            {/* Render parent icon */}
                            <div className="mr-2 flex-shrink-0">
                              {renderCategoryIcon(parent, 14) || (
                                <CategoryIconRenderer category={parent.category} size={14} />
                              )}
                            </div>

                            <span className="truncate">{parent.display_name}</span>
                          </Button>

                          {/* Subcategories (if expanded) */}
                          {hasSubcategories && isExpanded && (
                            <div className="ml-4 mt-0.5 space-y-0.5">
                              {/* Render subcategories */}
                              {(mergedCategoryHierarchy.children.get(parent.category) || []).map(
                                sub => {
                                  const isSubSelected =
                                    selectedProductCategory === parent.category &&
                                    selectedSubcategory === sub.subcategory;

                                  return (
                                    <Button
                                      key={sub.id}
                                      variant={isSubSelected ? 'secondary' : 'ghost'}
                                      size="sm"
                                      className={cn(
                                        'w-full justify-start h-7 px-2 text-[11px] font-normal',
                                        isSubSelected &&
                                          'bg-primary/10 text-primary hover:bg-primary/20',
                                      )}
                                      onClick={() =>
                                        handleProductCategorySelect(
                                          parent.category,
                                          sub.subcategory,
                                        )
                                      }
                                    >
                                      <div className="mr-2 flex-shrink-0 opacity-75">
                                        {renderCategoryIcon(sub, 12) ||
                                          renderCategoryIcon(parent, 12) || (
                                            <CategoryIconRenderer
                                              category={parent.category}
                                              size={12}
                                              className="opacity-75"
                                            />
                                          )}
                                      </div>
                                      <span className="truncate">{sub.display_name}</span>
                                    </Button>
                                  );
                                },
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* AI Product Creation */}
                  <Separator className="mt-3 mb-2" />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start h-8 px-2 text-xs font-normal hover:bg-primary/5"
                    onClick={() => {
                      setAiProductModalOpen(true);
                      setShowProductSubmenu(false);
                    }}
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-2 text-primary" />
                    New Product (AI)
                  </Button>
                </div>,
                document.body,
              )}
          </div>

          {/* Cable Tool with Submenu */}
          <div
            className="relative"
            onMouseEnter={hasScale ? handleCableMouseEnter : undefined}
            onMouseLeave={hasScale ? handleCableMouseLeave : undefined}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  ref={cableButtonRef}
                  variant={isCablePathMode || isCableJunctionMode ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => {
                    if (hasScale) {
                      setShowCableSubmenu(!showCableSubmenu);
                    }
                  }}
                  className={cn(
                    'relative',
                    isCablePathMode || isCableJunctionMode
                      ? 'bg-primary text-primary-foreground'
                      : '',
                    !hasScale && 'opacity-50 cursor-not-allowed',
                  )}
                  disabled={!hasScale}
                >
                  <Cable className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {!hasScale ? (
                  <div className="space-y-1">
                    <p className="font-medium">Cable Tools</p>
                    <p className="text-xs text-muted-foreground">Set floor plan scale first</p>
                  </div>
                ) : (
                  <p>Cable Tools</p>
                )}
              </TooltipContent>
            </Tooltip>

            {/* Cable Submenu */}
            {showCableSubmenu &&
              typeof document !== 'undefined' &&
              createPortal(
                <div
                  ref={cableSubmenuRef}
                  className="fixed flex min-w-[130px] flex-col space-y-1 rounded-md border border-slate-200/80 bg-white p-2 shadow-[0_18px_45px_rgba(15,23,42,0.16)] z-[1100]"
                  style={getSubmenuStyle(cableButtonRef)}
                  onMouseEnter={handleCableMouseEnter}
                  onMouseLeave={handleCableMouseLeave}
                >
                  {/* Bridge element */}
                  <div className="absolute -left-2 top-0 h-full w-2" />

                  {/* Path option */}
                  <Button
                    variant={isCablePathMode ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'justify-start text-sm',
                      isCablePathMode ? 'bg-primary text-primary-foreground' : '',
                    )}
                    onClick={() => handleCableToolSelect('path')}
                  >
                    <Cable className="h-4 w-4 mr-2" />
                    Path
                  </Button>

                  {/* Box option */}
                  <Button
                    variant={isCableJunctionMode ? 'default' : 'ghost'}
                    size="sm"
                    className={cn(
                      'justify-start text-sm',
                      isCableJunctionMode ? 'bg-primary text-primary-foreground' : '',
                    )}
                    onClick={() => handleCableToolSelect('box')}
                  >
                    <Square className="h-4 w-4 mr-2" />
                    Box
                  </Button>
                </div>,
                document.body,
              )}
          </div>

          {/* Add/Place Button - Opens Command Palette */}
          {onPaletteOpen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onPaletteOpen}
                  className="hover:bg-primary/10 hover:text-primary"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>Add Item (⌘K)</p>
              </TooltipContent>
            </Tooltip>
          )}

          <Separator className="my-1" />

          {/* Room Creation Tool */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={selectedTool === 'create-room' ? 'default' : 'ghost'}
                size="icon"
                onClick={handleRoomToolClick}
                className={
                  selectedTool === 'create-room' ? 'bg-primary text-primary-foreground' : ''
                }
              >
                <Grid2X2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Create Room</p>
            </TooltipContent>
          </Tooltip>

          {/* Elevation Views Tool */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isElevationMode ? 'default' : 'ghost'}
                size="icon"
                onClick={handleElevationToolClick}
                className={cn(
                  'relative',
                  isElevationMode ? 'bg-primary text-primary-foreground' : '',
                  (!hasScale || !hasRooms) && 'opacity-50 cursor-not-allowed',
                )}
                disabled={!hasScale || !hasRooms}
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {!hasScale ? (
                <div className="space-y-1">
                  <p className="font-medium">Elevation Views</p>
                  <p className="text-xs text-muted-foreground">Set floor plan scale first</p>
                </div>
              ) : !hasRooms ? (
                <div className="space-y-1">
                  <p className="font-medium">Elevation Views</p>
                  <p className="text-xs text-muted-foreground">Create at least one room first</p>
                </div>
              ) : (
                <p>Elevation Views</p>
              )}
            </TooltipContent>
          </Tooltip>

          {/* Scale & Measurement Tool */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={isScaleModeActive || isMeasuring ? 'default' : 'ghost'}
                size="icon"
                onClick={handleScaleToolClick}
                className={cn(
                  'relative',
                  isScaleModeActive || isMeasuring ? 'bg-primary text-primary-foreground' : '',
                )}
              >
                <Ruler className="h-4 w-4" />
                {isMeasuring && (
                  <div className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full animate-pulse" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Scale & Measurement</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* AI Product Creation Modal */}
      <AIProductCreationModal open={aiProductModalOpen} onOpenChange={setAiProductModalOpen} />
    </TooltipProvider>
  );
}
